import React, { memo } from "react"
import { Tag } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { JOB_STATUS, JobListResponse } from "types/automation.types"
import { getStatusIcon, getStatusColor } from "utils/automationUtils"
import { faRectangleXmark } from "@fortawesome/free-solid-svg-icons"

interface OngoingJobItemProps {
  /** Job data to display */
  job: JobListResponse
  /** Whether this job is currently selected */
  isSelected?: boolean
  /** Callback when job is clicked */
  onClick: (jobId: string) => void
  cancel: (jobId: string) => void
}

/**
 * Component to display an ongoing job item in the list
 * Memoized to prevent unnecessary rerenders
 */
const OngoingJobItem: React.FC<OngoingJobItemProps> = memo(
  ({ job, isSelected = false, onClick, cancel }) => {
    const handleClick = () => {
      onClick(job.job_id)
    }

    const handleCancel = (e: React.MouseEvent) => {
      e.stopPropagation() // Prevent triggering the onClick handler
      cancel(job.job_id)
    }

    return (
      <div
        className={`cursor-pointer transition-colors duration-200 border rounded-lg mb-2 p-3 flex items-center gap-4 ${
          isSelected
            ? "bg-blue-100 border-blue-300"
            : "bg-blue-50 border-blue-200 hover:bg-blue-100"
        }`}
        onClick={handleClick}
      >
        {/* Status Icon */}
        <div className="flex-shrink-0">
          <FontAwesomeIcon
            icon={getStatusIcon(job.status)}
            className={`text-lg ${
              job.status === JOB_STATUS.RUNNING ? "fa-spin" : ""
            }`}
            style={{ color: getStatusColor(job.status) }}
          />
        </div>

        {/* Job Details */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h4 className="font-medium text-gray-900 truncate m-0 mb-2">
            {job.script}
          </h4>

          {/* Job Info */}
          <div className="text-sm text-gray-600 space-y-1 mb-2">
            <div>Job ID: {job.job_id}</div>
            <div>User: {job.user_name}</div>
            <div>Unit: {job.unit_name}</div>
            {job.elapsed_seconds && <div>Elapsed: {job.elapsed_seconds}s</div>}
          </div>

          {/* Click to view console - moved to bottom */}
          <div className="text-xs text-gray-400">Click to view console</div>
        </div>

        {/* Status and Actions */}
        <div className="flex flex-row items-center gap-2 flex-shrink-0">
          <Tag color={getStatusColor(job.status)}>{job.status}</Tag>

          <FontAwesomeIcon
            onClick={handleCancel}
            icon={faRectangleXmark}
            className="text-red-500 cursor-pointer hover:text-red-700"
          />
        </div>
      </div>
    )
  }
)

OngoingJobItem.displayName = "OngoingJobItem"

export default OngoingJobItem
